import { useState } from "react";

import { SubdivisionType } from "shared/hooks";

import { TimeLine } from "shared/components";
import { SubdivisionTimelineItem } from "./SubdivisionTimelineItem";
import {
  SubdivisionTimelineItemContent,
  type SubdivisionTimelineItemContentProps,
} from "./SubdivisionTimelineItemContent";

const contentMap = {
  [SubdivisionType["subdivision of an approved development"]]: {
    planningApproval: {
      cost: "$500–$2,300+",
      timeframe: "1–2 months",
    },
    certification: {
      cost: "$3,000+",
      timeframe: "1–2 months",
    },
    works: {
      cost: "$1,800–$4,500 per lot",
      timeframe: "1–2 months",
    },
    statementOfCompliance: {
      cost: null,
      timeframe: "2–4 weeks",
    },
    titleRegistration: {
      cost: "$2,000–$4,000+",
      timeframe: "1–2 months",
    },
  },
  [SubdivisionType["battle-axe subdivision"]]: {
    planningApproval: {
      cost: "$5,000+",
      timeframe: "3–6 months",
    },
    certification: {
      cost: "$3,000+",
      timeframe: "1–2 months",
    },
    works: {
      cost: "$17,000–$45,000+",
      timeframe: "3–6 months",
    },
    statementOfCompliance: {
      cost: null,
      timeframe: "2–4 weeks",
    },
    titleRegistration: {
      cost: "$2,000–$4,000+",
      timeframe: "1–2 months",
    },
  },
  [SubdivisionType["large lot subdivision in a rural area"]]: {
    planningApproval: {
      cost: "$5,800–$13,800+",
      timeframe: "3–6 months",
    },
    certification: {
      cost: "$3,000+",
      timeframe: "1–2 months",
    },
    works: {
      cost: "$100,000+",
      timeframe: "3–8 months",
    },
    statementOfCompliance: {
      cost: null,
      timeframe: "1–2 months",
    },
    titleRegistration: {
      cost: "$3,000–$5,000+",
      timeframe: "1–2 months",
    },
  },
  [SubdivisionType["revising existing boundaries"]]: {
    planningApproval: {
      cost: "$5,00–$2,300+",
      timeframe: "1–2 months",
    },
    certification: {
      cost: "$3,000+",
      timeframe: "1–2 months",
    },
    works: {
      cost: "$1,800-$4,500+",
      timeframe: "1–2 months",
    },
    statementOfCompliance: {
      cost: null,
      timeframe: "2–4 weeks",
    },
    titleRegistration: {
      cost: "$2,000–$4,000+",
      timeframe: "1–2 months",
    },
  },
};

export interface SubdivisionTimelineProps {
  subdivisionType: SubdivisionTimelineItemContentProps["subdivisionType"];
}

export function SubdivisionTimeline({
  subdivisionType,
}: SubdivisionTimelineProps) {
  const [selectedId, setSelectedId] = useState<string>("1");

  if (!contentMap[subdivisionType]) {
    console.log("No content for subdivision type:", subdivisionType);
    return null;
  }

  return (
    <div>
      <TimeLine
        iconClassName="text-bodysm p-2.5 bg-textWhite border-blueDefault border-2 text-blueDisabled data-[selected=true]:bg-blueDefault data-[selected=true]:text-textWhite"
        itemClassName="data-[selected=true]:bg-transparent"
        items={[
          {
            content: "",
            id: "1",
            icon: "1",
            title: (
              <div className="pb-5">
                <SubdivisionTimelineItem
                  cost={contentMap[subdivisionType].planningApproval.cost}
                  id="1"
                  onOpenChange={setSelectedId}
                  selectedId={selectedId}
                  timeframe={
                    contentMap[subdivisionType].planningApproval.timeframe
                  }
                  title="Planning approval"
                >
                  <SubdivisionTimelineItemContent
                    subdivisionType={subdivisionType}
                    itemName="planningApproval"
                  />
                </SubdivisionTimelineItem>
              </div>
            ),
          },
          {
            content: "",
            id: "2",
            icon: "2",
            title: (
              <div className="pb-5">
                <SubdivisionTimelineItem
                  cost={contentMap[subdivisionType].certification.cost}
                  id="2"
                  onOpenChange={setSelectedId}
                  selectedId={selectedId}
                  timeframe={
                    contentMap[subdivisionType].certification.timeframe
                  }
                  title="Certification"
                >
                  <SubdivisionTimelineItemContent
                    subdivisionType={subdivisionType}
                    itemName="certification"
                  />
                </SubdivisionTimelineItem>
              </div>
            ),
          },
          {
            content: "",
            id: "3",
            icon: "3",
            title: (
              <div className="pb-5">
                <SubdivisionTimelineItem
                  cost={contentMap[subdivisionType].works.cost}
                  id="3"
                  onOpenChange={setSelectedId}
                  selectedId={selectedId}
                  timeframe={contentMap[subdivisionType].works.timeframe}
                  title="Works"
                >
                  <SubdivisionTimelineItemContent
                    subdivisionType={subdivisionType}
                    itemName="works"
                  />
                </SubdivisionTimelineItem>
              </div>
            ),
          },
          {
            content: "",
            id: "4",
            icon: "4",
            title: (
              <div className="pb-5">
                <SubdivisionTimelineItem
                  cost={contentMap[subdivisionType].statementOfCompliance.cost}
                  id="4"
                  onOpenChange={setSelectedId}
                  selectedId={selectedId}
                  timeframe={
                    contentMap[subdivisionType].statementOfCompliance.timeframe
                  }
                  title="Statement of compliance"
                >
                  <SubdivisionTimelineItemContent
                    subdivisionType={subdivisionType}
                    itemName="statementOfCompliance"
                  />
                </SubdivisionTimelineItem>
              </div>
            ),
          },
          {
            content: "",
            id: "5",
            icon: "5",
            title: (
              <div className="pb-5">
                <SubdivisionTimelineItem
                  cost={contentMap[subdivisionType].titleRegistration.cost}
                  id="5"
                  onOpenChange={setSelectedId}
                  selectedId={selectedId}
                  timeframe={
                    contentMap[subdivisionType].titleRegistration.timeframe
                  }
                  title="Title registration"
                >
                  <SubdivisionTimelineItemContent
                    subdivisionType={subdivisionType}
                    itemName="titleRegistration"
                  />
                </SubdivisionTimelineItem>
              </div>
            ),
          },
        ]}
        selectedId={selectedId}
      />
    </div>
  );
}
